import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkedAlt, faUsers, faBuilding, faAward } from '@fortawesome/free-solid-svg-icons';
import './About.css';

const CountUp: React.FC<{ end: number; suffix?: string; duration?: number }> = ({ end, suffix = '', duration = 2.5 }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView && ref.current) {
      let startTime: number | null = null;
      let animationFrame: number;

      const animateCount = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);

        // easeOutQuart
        const easeProgress = 1 - Math.pow(1 - progress, 4);

        if (ref.current) {
          const currentCount = Math.round(easeProgress * end);
          ref.current.textContent = currentCount.toLocaleString() + suffix;
        }

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animateCount);
        } else {
          // Ensure it finishes exactly on the target number
          if (ref.current) {
            ref.current.textContent = end.toLocaleString() + suffix;
          }
        }
      };

      animationFrame = requestAnimationFrame(animateCount);
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [isInView, end, duration, suffix]);

  return <span ref={ref}>0{suffix}</span>;
};

const About: React.FC = () => {
  return (
    <section id="about" className="about">
      <div className="about-container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="about-header"
        >
          <h2 className="section-title">ANSUVARA GROUP</h2>
          <p className="section-subtitle">Building the Future of Business in Pokhara</p>
        </motion.div>

        <div className="about-content">
          {/* Our Story Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="about-text"
          >
            <h3>Our Story</h3>
            <p>
              Established in 1993, Ansuvara Group is a distinguished conglomerate based in Nepal, with a diversified
              portfolio spanning real estate, housing, hospitality, financial services, and more. With a legacy built over
              three decades, the group has remained committed to fostering entrepreneurship, supporting national
              economic development, and adapting to the evolving global market landscape.
            </p>
            <p>
              The origins of Ansuvara Group trace back to a modest investment of NPR 5,000 and a small-scale
              venture in motorcycle rentals, established by the Group's Chairman, Mr. Bindu Kumar Thapa.
            </p>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/about" className="btn btn-primary">
                Learn More
              </Link>
            </motion.div>
          </motion.div>

          {/* Statistics Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="stats-section-about"
          >
            <div className="stats-grid-about">
              <div className="stat-item-about">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faMapMarkedAlt} />
                </div>
                <div className="stat-number"><CountUp end={2000} suffix="+" /></div>
                <div className="stat-label">Ropanis Land Development</div>
              </div>
              <div className="stat-item-about">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="stat-number"><CountUp end={500} suffix="+" /></div>
                <div className="stat-label">Jobs Created</div>
              </div>
              <div className="stat-item-about">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faBuilding} />
                </div>
                <div className="stat-number"><CountUp end={76} suffix="+" /></div>
                <div className="stat-label">Projects Handover</div>
              </div>
              <div className="stat-item-about">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faAward} />
                </div>
                <div className="stat-number"><CountUp end={30} suffix="+" /></div>
                <div className="stat-label">Years of Excellence</div>
              </div>
            </div>
          </motion.div>

          {/* Logo Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="logo-section-header"
          >
            <h3>Our Logo</h3>

            <img src="/logo.png" alt="Ansuvara Group" className="logo-section-image" />

            <p>
              The name Ansuvara is inspired by the diacritic sign Bindu, a symbol representing the point of origin
              from which energy radiates to illuminate the universe. This powerful concept reflects the Group's
              commitment to growth, vision, and positive impact.
              The Ansuvara Group logo thoughtfully integrates the initials 'A' for Ansuvara and 'G' for Group,
              symbolizing unity and strength. At its center lies the dot, or Bindu, a meaningful tribute to our
              Chairman, Mr. Bindu Kumar Thapa, whose visionary leadership continues to guide and inspire the
              organization's journey.
            </p>

          </motion.div>
        </div>


      </div>
    </section>
  );
};

export default About; 